import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { useInfoItemsActions, useAlerts, MESSAGE, FILES, TAGS, SUB_PATIENT, FILE_ID } from 'store/infoItems';
import Spinner from 'components/FullScreenSpinner';
import Button from 'components/Button';
import Message from 'components/Message';
import AnimateBox from 'components/AnimateBox';
import PlusIcon from 'assets/svg-icons/PlusDropZone';
import FileForm from '../FileForm';
import { DropZone } from '../styles';

import { MessageArea, Wrapper, P } from './styles';

const initialValues = {
  [MESSAGE]: '',
  [FILES]: [],
};

const Creator = ({ isOpenMenu }) => {
  const { id } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const { uploadMedia, createInfoItem } = useInfoItemsActions();
  const uploadAlerts = useAlerts();
  const createAlerts = useAlerts();

  const onSubmit = useCallback(
    (data) => {
      createAlerts.extractId(
        createInfoItem({
          [SUB_PATIENT]: id,
          [MESSAGE]: data[MESSAGE],
          [FILES]: data[FILES].map((item) => ({
            ...item,
            [TAGS]: item[TAGS] || [],
          })),
        })
      );
    },
    [createAlerts, createInfoItem, id]
  );

  const { values, setValues, handleSubmit, getFieldProps, resetForm } = useFormik({
    initialValues,
    onSubmit,
  });

  const onFocus = useCallback(() => setIsOpen(true), []);
  const onDrop = useCallback((data) => uploadAlerts.extractId(uploadMedia(data)), [uploadAlerts, uploadMedia]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const setFiles = useCallback(
    (arg) => setValues(($) => ({ ...$, [FILES]: typeof arg === 'function' ? arg($[FILES]) : arg })),
    [setValues]
  );

  useEffect(() => {
    if (uploadAlerts.success) {
      setFiles(($) => [...$, ...uploadAlerts.success]);
      uploadAlerts.resetAlerts();
    }
  }, [setFiles, uploadAlerts, values]);

  useEffect(() => {
    if (createAlerts.success) {
      resetForm({ values: initialValues });
      setIsOpen(false);
      createAlerts.resetAlerts();
    }
  }, [resetForm, createAlerts]);

  useEffect(() => {
    if (!isOpenMenu && isOpen) {
      setIsOpen(false);
    }
  }, [isOpenMenu, isOpen]);

  return (
    <form onSubmit={handleSubmit}>
      <MessageArea name={MESSAGE} isOpen={isOpen} {...getFieldProps(MESSAGE)} onFocus={onFocus} placeholder="Update erfassen" />
      <AnimateBox isOpen={isOpen}>
        <Wrapper>
          <DropZone isActive={isDragActive} {...getRootProps()}>
            {uploadAlerts.loading ? (
              <Spinner />
            ) : (
              <>
                <input {...getInputProps()} />
                <PlusIcon />
                <P isActive={isDragActive}>Dateien in dieses</P>
                <P isActive={isDragActive}>Feld ziehen</P>
              </>
            )}
          </DropZone>
          {values[FILES].map((item) => (
            <FileForm key={item[FILE_ID]} fileData={item} setFiles={setFiles} attachments={values[FILES]} />
          ))}
          <Button type="submit" color="success" height="48px" mt={12} disabled={createAlerts.loading || uploadAlerts.loading}>
            Eintrag erstellen
          </Button>
          {(createAlerts.error || uploadAlerts.error) && (
            <Message type="error">{createAlerts.error || uploadAlerts.error}</Message>
          )}
        </Wrapper>
      </AnimateBox>
    </form>
  );
};

Creator.propTypes = {
  isOpenMenu: PropTypes.bool.isRequired,
};

export default Creator;
